<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import CaptainPaywall from 'next/captain/pageComponents/Paywall.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import GeneralTab from './tabs/GeneralTab.vue';
import CopilotTab from './tabs/CopilotTab.vue';
import InsightsTab from './tabs/InsightsTab.vue';
import UsageTab from './tabs/UsageTab.vue';
import BrainBrandText from 'next/captain/brain/BrainBrandText.vue';

const { t } = useI18n();
const { captainEnabled } = useCaptain();

const captainConfigStore = useCaptainConfigStore();
const { uiFlags } = storeToRefs(captainConfigStore);

const isLoading = computed(() => uiFlags.value.isFetching);
const isSaving = computed(() => uiFlags.value.isSavingAiSettings);

// --- Tabs ---
const activeTab = ref('general');

const tabs = computed(() => [
  {
    key: 'general',
    label: t('CAPTAIN_SETTINGS.TABS.GENERAL'),
    icon: 'i-lucide-settings',
  },
  {
    key: 'copilot',
    label: t('CAPTAIN_SETTINGS.TABS.COPILOT.LABEL'),
    icon: 'i-lucide-message-square-plus',
  },
  {
    key: 'insights',
    label: t('CAPTAIN_SETTINGS.TABS.INSIGHTS.LABEL'),
    icon: 'i-lucide-lightbulb',
  },
  {
    key: 'usage',
    label: t('CAPTAIN_SETTINGS.TABS.USAGE.LABEL'),
    icon: 'i-lucide-bar-chart-3',
  },
]);

// --- Local state for AI Settings ---
const localAiSettings = reactive({
  provider: 'openai',
  apiKey: '',
  model: '',
  monthlyTokenLimit: null,
});

const aiSettingsLoaded = reactive({ value: false });
const aiSettingsDirty = computed(() => {
  if (!aiSettingsLoaded.value) return false;
  const server = captainConfigStore.aiSettings;
  return (
    localAiSettings.provider !== (server.provider || 'openai') ||
    localAiSettings.apiKey !== '' ||
    localAiSettings.model !== (server.model || '') ||
    localAiSettings.monthlyTokenLimit !== (server.monthly_token_limit || null)
  );
});

// --- Local state for Features ---
const pendingFeatureChanges = reactive({});
const pendingModelChanges = reactive({});
const featuresDirty = computed(
  () =>
    Object.keys(pendingFeatureChanges).length > 0 ||
    Object.keys(pendingModelChanges).length > 0
);

// --- Local state for JSONB config (copilot, insights, alerts) ---
const pendingConfigChanges = reactive({});
const configDirty = computed(
  () => Object.keys(pendingConfigChanges).length > 0
);

const localCopilotConfig = computed(() => {
  const serverConfig = captainConfigStore.aiSettings.config?.copilot || {};
  const pending = pendingConfigChanges.copilot || {};
  return { ...serverConfig, ...pending };
});

const localInsightsConfig = computed(() => {
  const serverConfig = captainConfigStore.aiSettings.config?.insights || {};
  const pending = pendingConfigChanges.insights || {};
  return { ...serverConfig, ...pending };
});

const localAlertsConfig = computed(() => {
  const serverConfig = captainConfigStore.aiSettings.config?.alerts || {};
  const pending = pendingConfigChanges.alerts || {};
  return { ...serverConfig, ...pending };
});

const hasUnsavedChanges = computed(
  () => aiSettingsDirty.value || featuresDirty.value || configDirty.value
);

// --- Sync server AI settings to local ---
watch(
  () => captainConfigStore.aiSettings,
  settings => {
    if (settings && Object.keys(settings).length > 0) {
      localAiSettings.provider = settings.provider || 'openai';
      localAiSettings.apiKey = '';
      localAiSettings.model = settings.model || '';
      localAiSettings.monthlyTokenLimit = settings.monthly_token_limit || null;
      aiSettingsLoaded.value = true;
    }
  },
  { immediate: true }
);

// --- Event handlers ---
function handleFeatureToggle({ feature, enabled }) {
  pendingFeatureChanges[feature] = enabled;
}

function handleModelChange({ feature, model }) {
  pendingModelChanges[feature] = model;
}

function handleConfigChange({ section, data }) {
  if (!pendingConfigChanges[section]) {
    pendingConfigChanges[section] = {};
  }
  Object.assign(pendingConfigChanges[section], data);
}

async function handleSave() {
  try {
    const promises = [];

    if (aiSettingsDirty.value || configDirty.value) {
      const payload = {};
      if (aiSettingsDirty.value) {
        payload.provider = localAiSettings.provider;
        payload.model = localAiSettings.model;
        payload.monthly_token_limit = localAiSettings.monthlyTokenLimit;
        if (localAiSettings.apiKey) {
          payload.api_key = localAiSettings.apiKey;
        }
      }
      if (configDirty.value) {
        payload.config = { ...pendingConfigChanges };
      }
      promises.push(captainConfigStore.updateAiSettings(payload));
    }

    if (featuresDirty.value) {
      const data = {};
      if (Object.keys(pendingFeatureChanges).length > 0) {
        data.captain_features = { ...pendingFeatureChanges };
      }
      if (Object.keys(pendingModelChanges).length > 0) {
        data.captain_models = { ...pendingModelChanges };
      }
      promises.push(captainConfigStore.updatePreferences(data));
    }

    await Promise.all(promises);

    // Clear pending changes
    Object.keys(pendingFeatureChanges).forEach(
      k => delete pendingFeatureChanges[k]
    );
    Object.keys(pendingModelChanges).forEach(
      k => delete pendingModelChanges[k]
    );
    Object.keys(pendingConfigChanges).forEach(
      k => delete pendingConfigChanges[k]
    );
    localAiSettings.apiKey = '';

    await captainConfigStore.fetchAiSettings();
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
  }
}

// --- Lifecycle ---
onMounted(() => {
  captainConfigStore.fetch();
  captainConfigStore.fetchAiSettings();
  captainConfigStore.fetchUsage();
});
</script>

<template>
  <!-- Loading state -->
  <div
    v-if="isLoading"
    class="flex items-center justify-center w-full h-full"
  >
    <woot-loading-state :message="t('CAPTAIN_SETTINGS.LOADING')" />
  </div>

  <!-- Main layout (mirrors BrainAssistantEditor pattern) -->
  <div v-else class="flex flex-col w-full h-full overflow-hidden">
    <!-- Header (fixed) -->
    <BaseSettingsHeader
      :title="t('CAPTAIN_SETTINGS.TITLE')"
      :description="t('CAPTAIN_SETTINGS.DESCRIPTION')"
      :link-text="t('CAPTAIN_SETTINGS.LINK_TEXT')"
      feature-name="captain_billing"
    >
      <template #title>
        <span class="inline-flex items-center gap-2">
          <span
            class="i-ph-sparkle-fill size-5 text-n-brand flex-shrink-0"
          />
          <BrainBrandText :text="t('CAPTAIN_SETTINGS.TITLE')" />
        </span>
      </template>
      <template #description>
        <BrainBrandText :text="t('CAPTAIN_SETTINGS.DESCRIPTION')" />
      </template>
    </BaseSettingsHeader>

    <template v-if="captainEnabled">
      <!-- Tab Bar (fixed, outside scroll area) -->
      <div
        class="flex border-b border-n-weak mt-8 overflow-x-auto scrollbar-hide"
      >
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap flex-shrink-0"
          :class="
            activeTab === tab.key
              ? 'border-n-brand text-n-brand'
              : 'border-transparent text-n-slate-10 hover:text-n-slate-11'
          "
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab Content (scrollable, constrained width) -->
      <main class="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
        <div class="w-full max-w-[60rem] py-6">
          <GeneralTab
            v-if="activeTab === 'general'"
            :local-ai-settings="localAiSettings"
            @feature-toggle="handleFeatureToggle"
            @model-change="handleModelChange"
          />

          <CopilotTab
            v-else-if="activeTab === 'copilot'"
            :copilot-config="localCopilotConfig"
            @feature-toggle="handleFeatureToggle"
            @model-change="handleModelChange"
            @config-change="handleConfigChange"
          />

          <InsightsTab
            v-else-if="activeTab === 'insights'"
            :insights-config="localInsightsConfig"
            @feature-toggle="handleFeatureToggle"
            @model-change="handleModelChange"
            @config-change="handleConfigChange"
          />

          <UsageTab
            v-else-if="activeTab === 'usage'"
            :alerts-config="localAlertsConfig"
            @config-change="handleConfigChange"
          />
        </div>
      </main>

      <!-- Save Footer (fixed at bottom, outside scroll area) -->
      <footer
        class="flex items-center justify-between py-4 border-t border-n-weak bg-n-surface-1"
      >
        <span v-if="hasUnsavedChanges" class="text-sm text-n-amber-11">
          {{ t('CAPTAIN_SETTINGS.UNSAVED_CHANGES') }}
        </span>
        <span v-else />
        <Button
          :label="isSaving ? t('CAPTAIN_SETTINGS.SAVING') : t('CAPTAIN_SETTINGS.SAVE')"
          :is-loading="isSaving"
          :disabled="!hasUnsavedChanges"
          @click="handleSave"
        />
      </footer>
    </template>

    <div v-else class="mt-8">
      <CaptainPaywall />
    </div>
  </div>
</template>
